// Helper function to format date/time based on user locale
export const formatDateTime = (dateString) => {
  const userLocale = navigator.language || 'en-US';
  const isPolish = userLocale.startsWith('pl');
  
  // Parse datetime string manually to avoid timezone conversion
  // Expected format: "YYYY-MM-DDTHH:MM:SS" or "YYYY-MM-DDTHH:MM"
  const parts = dateString.split('T');
  if (parts.length < 2) {
    // Fallback if format is unexpected
    const date = new Date(dateString);
    return date.toLocaleString(userLocale);
  }
  
  const datePart = parts[0]; // YYYY-MM-DD
  const timePart = parts[1].split(':'); // [HH, MM, SS or just HH, MM]
  const dateParts = datePart.split('-'); // [YYYY, MM, DD]
  
  const year = dateParts[0];
  const month = dateParts[1];
  const day = dateParts[2];
  const hours24 = parseInt(timePart[0]);
  const minutes = timePart[1];
  
  if (isPolish) {
    // Polish format: HH:mm, DD.MM.YY
    const hoursStr = String(hours24).padStart(2, '0');
    const yearShort = year.slice(-2);
    
    return `${hoursStr}:${minutes}, ${day}.${month}.${yearShort}`;
  } else {
    // English/International format: M/D/YY, h:mm AM/PM
    const hours12 = hours24 % 12 || 12;
    const ampm = hours24 >= 12 ? 'PM' : 'AM';
    const monthInt = parseInt(month);
    const dayInt = parseInt(day);
    const yearShort = year.slice(-2);
    
    return `${monthInt}/${dayInt}/${yearShort}, ${hours12}:${minutes} ${ampm}`;
  }
};

export const calculateEventStatus = (startDateTime, endDateTime) => {
  const now = new Date();
  
  // Parse dates WITHOUT timezone conversion (same as formatDateTime)
  const parseDateTime = (dateString) => {
    const parts = dateString.split('T');
    const datePart = parts[0]; // YYYY-MM-DD
    const timePart = parts[1].split(':'); // [HH, MM, SS]
    const dateParts = datePart.split('-'); // [YYYY, MM, DD]
    
    const year = parseInt(dateParts[0]);
    const month = parseInt(dateParts[1]) - 1; // Month is 0-indexed
    const day = parseInt(dateParts[2]);
    const hours = parseInt(timePart[0]);
    const minutes = parseInt(timePart[1]);
    const seconds = timePart[2] ? parseInt(timePart[2]) : 0;
    
    return new Date(year, month, day, hours, minutes, seconds);
  };
  
  const startDate = parseDateTime(startDateTime);
  const endDate = endDateTime ? parseDateTime(endDateTime) : null;
  
  const hasStarted = now >= startDate;
  const hasEnded = endDate && now >= endDate;
  const isOngoing = hasStarted && !hasEnded && endDate;
  
  let statusColor = '#959393'; // Gray - upcoming
  let statusText = 'Upcoming';
  let shouldPulse = true;
  
  if (hasEnded) {
    // Event has ended
    statusColor = '#ea4335'; // Red
    statusText = 'Finished';
    shouldPulse = false;
} else if (isOngoing) {
  // Event is currently ongoing
  const totalDuration = endDate - startDate;
  const remainingPercentage = ((endDate - now) / totalDuration) * 100;
  
  if (remainingPercentage >= 80) {
    statusColor = '#34a853'; // Green
    statusText = 'Started';
  } else if (remainingPercentage >= 30) {
    statusColor = '#fbbc04'; // Yellow
    statusText = 'Ongoing';
  } else {
    statusColor = '#ea4335'; // Red
    statusText = 'Finishing';
  }
} else {
    // Event hasn't started yet
    const hoursUntilStart = (startDate - now) / (1000 * 60 * 60);
    if (hoursUntilStart < 24) {
      statusColor = '#34a853'; // Green - starting soon
      statusText = 'Starting Soon';
    } else {
      statusColor = '#959393'; // Gray - upcoming
      statusText = 'Upcoming';
    }
  }
  
  return {
    statusColor,
    statusText,
    shouldPulse,
    hasStarted,
    hasEnded,
    isOngoing,
    now,
    startDate,
    endDate
  };
};

export const generateTimeOptions = () => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += 5) {
        // Skip 00:00
        if (hour === 0 && minute === 0) continue;
        
        const timeString = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        times.push(`<option value="${timeString}">${timeString}</option>`);
        }
    }
    return times.join('');
};

export const focusCurrentTime = (inputId) => {
  const timeInput = document.getElementById(inputId);
  if (!timeInput) return;

  if (inputId === 'startTime') {
    // Calculate current time rounded to next 5-minute interval
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = Math.ceil(now.getMinutes() / 5) * 5;
    const currentTime = `${String(currentHour).padStart(2, '0')}:${String(currentMinute === 60 ? 0 : currentMinute).padStart(2, '0')}`;

    // Auto-select the current time when opening
    if (!timeInput.value) {
      timeInput.value = currentTime;
    }
    return;
  }

  if (inputId === 'endTime') {
    // Open the end-time list scrolled to a sensible time AFTER the start time
    // (default: start + 30 minutes) instead of from 00:05 at the top.
    if (timeInput.value) return; // user already chose one — leave it
    const startEl = document.getElementById('startTime');
    if (!startEl || !startEl.value) return;

    const [sh, sm] = startEl.value.split(':').map(Number);
    let total = sh * 60 + sm + 30; // +30 minutes

    const startDateEl = document.getElementById('startDate');
    const endDateEl = document.getElementById('endDate');
    const sameDate =
      startDateEl && endDateEl && startDateEl.value && endDateEl.value &&
      startDateEl.value === endDateEl.value;

    if (total >= 24 * 60) {
      if (sameDate) {
        // No +1h slot the same day — fall back to the last slot if it's still
        // after the start time, otherwise leave blank (user must pick later date).
        total = 23 * 60 + 55;
        if (total <= sh * 60 + sm) return;
      } else {
        total = total % (24 * 60); // next-day time is fine for multi-day events
      }
    }

    const eh = Math.floor(total / 60);
    const em = total % 60;
    const candidate = `${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`;
    const hasOption = Array.from(timeInput.options).some(o => o.value === candidate);
    if (hasOption) {
      timeInput.value = candidate;
      if (window.updateEndTimeMin) window.updateEndTimeMin();
    }
  }
};

window.formatDateTime = formatDateTime;